import { useState, useEffect, Fragment } from 'react';
import {
  Autocomplete,
  Modal,
  Box,
  Stack,
  Typography,
  TextField,
  FormControl,
  FormControlLabel,
  Checkbox,
  Button,
  ButtonGroup,
  styled,
  InputLabel,
  CircularProgress,
  FormHelperText,
} from '@mui/material';
import Dropzone from '../../../../components/Dropzone/Dropzone';
import { GetProducerDownlines } from '../../../../api/admin';
import { useQuery, useMutation } from '@tanstack/react-query';
import DoneIcon from '@mui/icons-material/Done';
import ChecklistModal from './ChecklistModal';
import { SubmitContractingRequest } from '../../../../api/MyIMO';
import { useUser } from '../../../../context/UserContext';
import SubmitContractRequestFeedback from '../feedback/SubmitContractRequestFeedback';

const ModalContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  width: 500,
  minHeight: 500,
  maxHeight: 800,
  padding: '2rem',
  overflowY: 'scroll',
  borderRadius: 2,
  background: theme.palette.background.paper,
}));

const ModalSwitchButton = styled(Button, {
  shouldForwardProp: (prop) => prop !== '$isactive',
})(({ theme, $isactive }) => ({
  ...($isactive && {
    background: $isactive ? theme.palette.primary.main : 'none',
    color: $isactive ? 'white' : '',
    '&:hover': {
      background: theme.palette.primary.main,
    },
  }),
}));

const NewRequestModal = ({ showModal, toggleModal, producerID, refetchRequests }) => {
  const user = useUser();

  const formData = new FormData();
  const [requestType, setRequestType] = useState('New Agent');
  const [fetchDownlines, setFetchDownlines] = useState(false);
  const initFormErrors = {
    firstName: '',
    lastName: '',
    suffix: '',
    npn: '',
    requestInformation: '',
    producerID: '',
    files: '',
  };
  const initFormState = {
    requestInfo: {
      producerID: '',
      submitterNPN: user.npnID,
      requestType: 2,
      dataObject: {
        firstName: '',
        lastName: '',
        suffix: '',
        nationalProducerNumber: '',
        requestInformation: '',
        iProvide: false,
        iQumulate: false,
      },
    },
    isNewAgentRequest: false,
    documentsList: [],
  };
  const [agentPortalIRSubmissionRequest, setAgentPortalIRSubmissionRequest] =
    useState(initFormState);
  const [formErrors, setFormErrors] = useState(initFormErrors);
  const [checklistDialogOpen, setChecklistDialogOpen] = useState(false);

  const suffixOptions = [
    'Jr.',
    'Sr.',
    'II',
    'III',
    'IV',
    'Esq.',
    'Ph.D.',
    'M.D.',
    'D.D.S.',
    'CPA',
    'MBA',
    'RN',
    'PE',
    'Ret.',
  ];

  const getProducerDownlines = useQuery({
    queryKey: ['getProducerDownlinesMyIMO', producerID],
    queryFn: () => GetProducerDownlines({ ProducerID: producerID })(),
    enabled: !!fetchDownlines,
    select: (data) => {
      return data.filter((item) => item.down_ContractStatusDesc === 'Active');
    },
  });

  const {
    mutate,
    data: mutationResult,
    isSuccess: mutationSuccess,
    isError: mutationError,
    isPending: mutationIsLoading,
    reset: resetRequestForm,
  } = useMutation({
    mutationFn: (data) => SubmitContractingRequest(data)(),
  });

  const handleActiveButton = (requestType) => {
    setRequestType(requestType);
    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      requestInfo: {
        ...prev.requestInfo,
        producerID: '',
        requestType: 2,
        dataObject: {
          firstName: '',
          lastName: '',
          nationalProducerNumber: '',
          requestInformation: '',
          suffix: '',
          iProvide: false,
          iQumulate: false,
        },
      },
      documentsList: [],
    }));
  };

  const handleFormReset = () => {
    setAgentPortalIRSubmissionRequest(initFormState);
    resetRequestForm();
  };

  const handleFileUpload = (files) => {
    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      documentsList: files,
    }));

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      files: '',
    }));
  };

  const handleInput = (e) => {
    const { name, value } = e.target;
    const errorMsg = validateField(name, value);
    if (errorMsg) {
      setFormErrors((prevErrors) => ({
        ...prevErrors,
        [name]: errorMsg,
      }));
      return;
    }

    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      requestInfo: {
        ...prev.requestInfo,
        dataObject: {
          ...prev.requestInfo.dataObject,
          [name]: value,
        },
      },
    }));
    setFormErrors((prevErrors) => ({
      ...prevErrors,
      [name]: '',
    }));
  };

  const validateField = (name, value) => {
    const lettersOnly = /^[A-Za-zÀ-ÖØ-öø-ÿ\s'-]+$/u;

    switch (name) {
      case 'firstName':
        if (!lettersOnly.test(value)) return 'Only letters are allowed';
        return '';
      case 'lastName':
        if (!lettersOnly.test(value)) return 'Only letters are allowed';
        return '';
      case 'nationalProducerNumber':
        if (!/^\d+$/.test(value)) return 'NPN must be digits only';
        return '';
      default:
        return '';
    }
  };

  const validateForm = () => {
    const newErrors = {};
    let isValid = true;
    const { dataObject } = agentPortalIRSubmissionRequest.requestInfo;

    if (requestType === 'New Agent') {
      if (dataObject.firstName.trim() === '') {
        newErrors.firstName = 'First name is required';
        isValid = false;
      }
      if (dataObject.lastName.trim() === '') {
        newErrors.lastName = 'Last name is required';
        isValid = false;
      }
      if (dataObject.nationalProducerNumber.trim() === '') {
        newErrors.nationalProducerNumber = 'NPN is required';
        isValid = false;
      }
    }

    if (agentPortalIRSubmissionRequest.documentsList.length === 0) {
      newErrors.files = 'At least one file is required';
      isValid = false;
    }

    if (requestType === 'Existing Agent') {
      if (agentPortalIRSubmissionRequest.requestInfo.producerID.trim() === '') {
        newErrors.producerID = 'Producer ID is required';
        isValid = false;
      }
    }

    setFormErrors((prevErrors) => ({
      ...prevErrors,
      ...newErrors,
    }));

    return isValid;
  };

  const toggleChecklistDialog = () => {
    setChecklistDialogOpen((prev) => !prev);
  };

  useEffect(() => {
    setFormErrors(initFormErrors);
    setAgentPortalIRSubmissionRequest((prev) => ({
      ...prev,
      requestInfo: {
        ...prev.requestInfo,
        producerID: '',
        submitterNPN: user.npnID,
        dataObject: {
          firstName: '',
          lastName: '',
          nationalProducerNumber: '',
          requestInformation: '',
          suffix: '',
          iProvide: false,
          iQumulate: false,
        },
      },
    }));
  }, [requestType, producerID]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const formIsValid = validateForm();
    if (formIsValid) {
      const { requestInfo } = agentPortalIRSubmissionRequest;
      const isNewAgentRequest = requestType === 'New Agent' ? true : false;
      // append suffix to lastName if exists
      const lastName = requestInfo.dataObject.suffix
        ? `${requestInfo.dataObject.lastName} ${requestInfo.dataObject.suffix}`
        : requestInfo.dataObject.lastName;

      // flatten the request data for multipart / formdata
      formData.append('RequestInfo.ProducerID', requestInfo.producerID ?? '');
      formData.append('RequestInfo.SubmitterNPN', requestInfo.submitterNPN ?? '');
      formData.append('RequestInfo.RequestType', requestInfo.requestType ?? '');
      formData.append('RequestInfo.DataObject.FirstName', requestInfo.dataObject.firstName ?? '');
      formData.append('RequestInfo.DataObject.LastName', lastName ?? '');
      formData.append(
        'RequestInfo.DataObject.NationalProducerNumber',
        requestInfo.dataObject.nationalProducerNumber ?? ''
      );
      formData.append(
        'RequestInfo.DataObject.RequestInformation',
        requestInfo.dataObject.requestInformation ?? ''
      );
      formData.append('RequestInfo.DataObject.IProvide', requestInfo.dataObject.iProvide);
      formData.append('RequestInfo.DataObject.IQumulate', requestInfo.dataObject.iQumulate);
      formData.append('IsNewAgentRequest', String(isNewAgentRequest));

      agentPortalIRSubmissionRequest.documentsList.forEach((file) => {
        formData.append('DocumentsList', file, file.name);
      });

      mutate(formData);

      if (mutationSuccess) {
        setAgentPortalIRSubmissionRequest(initFormState);
        setFormErrors(initFormErrors);
      }
    }
  };

  const showFeedback = mutationIsLoading || mutationSuccess || mutationError;

  return (
    <Modal
      open={showModal}
      onClose={toggleModal}
      sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', margin: '1rem' }}
      keepMounted={true}
    >
      <ModalContainer>
        {showFeedback ? (
          <SubmitContractRequestFeedback
            mutationIsLoading={mutationIsLoading}
            mutationIsError={mutationError}
            mutationIsSuccess={mutationSuccess}
            mutationResult={mutationResult}
            toggleModal={toggleModal}
            resetRequestForm={handleFormReset}
            retryRequest={handleSubmit}
            refetchRequests={refetchRequests}
          />
        ) : (
          <>
            <ButtonGroup variant="outlined" aria-label="Basic button group">
              <ModalSwitchButton
                $isactive={requestType === 'New Agent'}
                onClick={() => handleActiveButton('New Agent')}
              >
                New Agent
              </ModalSwitchButton>
              <ModalSwitchButton
                $isactive={requestType === 'Existing Agent'}
                onClick={() => handleActiveButton('Existing Agent')}
              >
                Existing Agent
              </ModalSwitchButton>
            </ButtonGroup>
            <form>
              <Typography sx={{ padding: '1rem 0' }} variant="h6">
                New Contract Request
              </Typography>
              <Stack gap={2}>
                {requestType === 'New Agent' ? (
                  <>
                    <Stack gap={1} direction={'row'}>
                      <FormControl>
                        <TextField
                          onChange={handleInput}
                          error={formErrors.firstName ? true : false}
                          helperText={formErrors.firstName ? formErrors.firstName : ''}
                          id="firstName"
                          name="firstName"
                          label="First Name"
                        />
                      </FormControl>
                      <FormControl>
                        <TextField
                          onChange={handleInput}
                          error={formErrors.lastName ? true : false}
                          helperText={formErrors.lastName ? formErrors.lastName : ''}
                          id="lastName"
                          name="lastName"
                          label="Last Name"
                        />
                      </FormControl>
                    </Stack>
                    <Stack gap={1} direction={'row'}>
                      <FormControl sx={{ flex: 1 }}>
                        <Autocomplete
                          options={suffixOptions}
                          renderInput={(params) => (
                            <TextField {...params} label="Suffix" id="suffix" name="suffix" />
                          )}
                          onChange={(_, value) => {
                            setAgentPortalIRSubmissionRequest((prev) => ({
                              ...prev,
                              requestInfo: {
                                ...prev.requestInfo,
                                dataObject: {
                                  ...prev.requestInfo.dataObject,
                                  suffix: value,
                                },
                              },
                            }));
                          }}
                        />
                      </FormControl>
                      <FormControl sx={{ flex: 1 }}>
                        <TextField
                          onChange={handleInput}
                          error={formErrors.nationalProducerNumber ? true : false}
                          helperText={
                            formErrors.nationalProducerNumber
                              ? formErrors.nationalProducerNumber
                              : ''
                          }
                          id="nationalProducerNumber"
                          name="nationalProducerNumber"
                          label="NPN"
                        />
                      </FormControl>
                    </Stack>
                  </>
                ) : (
                  <>
                    <FormControl>
                      <FormHelperText sx={{ margin: 0, paddingBottom: '.5rem' }}>
                        Select the producer that you would like to dual-contract
                      </FormHelperText>
                      <Autocomplete
                        sx={{ textTransform: 'capitalize' }}
                        disablePortal
                        options={getProducerDownlines.data || []}
                        getOptionLabel={(option) =>
                          option?.down_ProducerId && option?.down_PrintName
                            ? `${option.down_ProducerId.trim()} ${option.down_PrintName.trim()}`
                            : ''
                        }
                        isOptionEqualToValue={(option, value) =>
                          option.down_ProducerId.trim() === value.down_ProducerId.trim()
                        }
                        onOpen={() => setFetchDownlines(true)}
                        onClose={() => setFetchDownlines(false)}
                        loading={getProducerDownlines.isPending}
                        value={
                          getProducerDownlines.data?.find(
                            (item) =>
                              item.down_ProducerId.trim() ===
                              agentPortalIRSubmissionRequest.requestInfo.producerID
                          ) || null
                        }
                        onChange={(_, value) => {
                          setAgentPortalIRSubmissionRequest((prev) => ({
                            ...prev,
                            requestInfo: {
                              ...prev.requestInfo,
                              producerID: value ? value.down_ProducerId.trim() : '',
                            },
                          }));
                          setFormErrors((prevErrors) => ({
                            ...prevErrors,
                            producerID: '',
                          }));
                        }}
                        slotProps={{
                          listBox: {
                            sx: {
                              maxHeight: 300,
                            },
                          },
                        }}
                        renderInput={(params) => (
                          <TextField
                            id="producerID"
                            name="producerID"
                            error={!!formErrors.producerID}
                            helperText={formErrors.producerID || ''}
                            {...params}
                            label="Producer"
                            sx={{ textTransform: 'capitalize' }}
                            InputProps={{
                              ...params.InputProps,
                              endAdornment: (
                                <Fragment>
                                  {getProducerDownlines.isPending ? (
                                    <CircularProgress color="inherit" size={20} />
                                  ) : null}
                                  {params.InputProps.endAdornment}
                                </Fragment>
                              ),
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </>
                )}
                <FormControl>
                  <TextField
                    onChange={handleInput}
                    id="requestInformation"
                    name="requestInformation"
                    label="Additional Information"
                    multiline
                    minRows={3}
                    maxRows={5}
                  />
                </FormControl>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Which product is this for? Select all that apply.
                  </Typography>
                  <Stack direction={'column'}>
                    <Stack direction={'row'} gap={1}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={agentPortalIRSubmissionRequest.requestInfo.dataObject.iProvide}
                            onChange={(e) => {
                              setAgentPortalIRSubmissionRequest((prev) => ({
                                ...prev,
                                requestInfo: {
                                  ...prev.requestInfo,
                                  dataObject: {
                                    ...prev.requestInfo.dataObject,
                                    iProvide: e.target.checked,
                                  },
                                },
                              }));
                            }}
                            name="iProvide"
                          />
                        }
                        label="IProvide (OLA)"
                      />
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={
                              agentPortalIRSubmissionRequest.requestInfo.dataObject.iQumulate
                            }
                            onChange={(e) => {
                              setAgentPortalIRSubmissionRequest((prev) => ({
                                ...prev,
                                requestInfo: {
                                  ...prev.requestInfo,
                                  dataObject: {
                                    ...prev.requestInfo.dataObject,
                                    iQumulate: e.target.checked,
                                  },
                                },
                              }));
                            }}
                            name="iQumulate"
                          />
                        }
                        label="IQumulate (MYGA)"
                      />
                    </Stack>
                  </Stack>
                </Box>
                <Stack>
                  <FormHelperText>Only PDF files are supported</FormHelperText>
                  <Dropzone
                    onFilesAccepted={handleFileUpload}
                    maxFiles={100}
                    accept={{ 'application/pdf': [] }}
                    dropzoneText="Drag & drop or click to select files."
                    clearFiles={requestType}
                  ></Dropzone>
                  {formErrors.files && (
                    <InputLabel
                      sx={{
                        margin: '0',
                        fontSize: '.75rem',
                      }}
                      variant="error"
                    >
                      {formErrors.files}
                    </InputLabel>
                  )}
                </Stack>
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'flex-end',
                    alignItems: 'center',
                    gap: '.5rem',
                  }}
                >
                  <Button onClick={toggleChecklistDialog} variant="text">
                    Review checklist
                  </Button>
                  <Button onClick={handleSubmit} variant="contained">
                    Submit
                  </Button>
                  <ChecklistModal
                    checklistDialogOpen={checklistDialogOpen}
                    toggleChecklistDialog={toggleChecklistDialog}
                  />
                </Box>
              </Stack>
            </form>
          </>
        )}
      </ModalContainer>
    </Modal>
  );
};

export default NewRequestModal;
